// ============================================================================
// 📋 COMPONENTE - TABELA FINANCEIRA — V3.0 LIGHT+DARK DEFINITIVO
// Stripe Transactions · Apple Wallet · Linear Issue List · Light + Dark mode
// ============================================================================
// 🔥 V3.0: Fondo contenedor #f1f5f9 en light — contraste real vs página #f9fafb
// 🔥 V3.0: DesktopRow con striped rows (bg-gray-100/70 en filas impares)
// 🔥 V3.0: MobileCard con borde 0.10 + sombra visible en light
// 🔥 V2: Soporte completo Light Mode + Dark Mode vía prop isDark
// ✅ Zero breaking changes — todas as props originais mantidas
// ============================================================================

import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  DollarSign,
  Trash2,
  FileText,
  MessageCircle,
  TrendingUp,
  TrendingDown,
  Plus,
  Home,
  Key,
  Tag,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import {
  formatMoney,
  formatDate,
  abbreviateCategory,
  getTypeColorClass,
  getTypeShortText,
  getTypeFullText,
} from "../utils/formatters";

// ─── spring config ────────────────────────────────────────────────────────────
const spring = { type: "spring", stiffness: 380, damping: 30 };

// ─── helpers ──────────────────────────────────────────────────────────────────

const fmt = (v) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v ?? 0);

const categoryIcon = (category = "") => {
  const c = category.toLowerCase();
  if (c.includes("aluguel") || c.includes("locação")) return Key;
  if (c.includes("imóvel") || c.includes("venda"))    return Home;
  if (c.includes("salário") || c.includes("receita")) return Wallet;
  return Tag;
};

// ─── Tooltip ──────────────────────────────────────────────────────────────────

const Tooltip = ({ label, children, isDark = true }) => {
  const [show, setShow] = React.useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.92 }}
            transition={{ duration: 0.13 }}
            className={`absolute -bottom-9 left-1/2 -translate-x-1/2 z-50
                       whitespace-nowrap px-2.5 py-1 rounded-lg text-xs font-medium shadow-xl pointer-events-none
                       ${isDark
                         ? "bg-gray-900 text-gray-100 border border-white/10 shadow-black/40"
                         : "bg-white text-gray-700 border border-gray-200 shadow-gray-200/60"
                       }`}
          >
            {label}
            <div className={`absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45
                            border-l border-t
                            ${isDark
                              ? "bg-gray-900 border-white/10"
                              : "bg-white border-gray-200"
                            }`} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── ShimmerBar ───────────────────────────────────────────────────────────────

const ShimmerBar = ({ isDark = true }) => (
  <motion.div
    className="absolute inset-0"
    style={{
      background: isDark
        ? "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.07) 50%, transparent 100%)"
        : "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.05) 50%, transparent 100%)",
    }}
    animate={{ x: ["-100%", "100%"] }}
    transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
  />
);

// ─── Skeleton Row ─────────────────────────────────────────────────────────────

const SkeletonRow = ({ index, isMobile, isDark = true }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: index * 0.07 }}
    className={`flex items-center gap-4 ${isMobile ? "p-4" : "px-6 py-4"}
                ${isDark ? "border-white/5" : "border-gray-200"} border-b`}
  >
    <div className={`w-8 h-8 rounded-xl shrink-0 overflow-hidden relative
                    ${isDark ? "bg-white/5" : "bg-gray-200"}`}>
      <ShimmerBar isDark={isDark} />
    </div>
    <div className="flex-1 flex flex-col gap-2">
      <div className={`h-3 rounded-full overflow-hidden relative ${isDark ? "bg-white/5" : "bg-gray-200"}`}
           style={{ width: `${55 + index * 7}%` }}>
        <ShimmerBar isDark={isDark} />
      </div>
      <div className={`h-2.5 rounded-full overflow-hidden relative ${isDark ? "bg-white/5" : "bg-gray-200"}`}
           style={{ width: "35%" }}>
        <ShimmerBar isDark={isDark} />
      </div>
    </div>
    {!isMobile && (
      <>
        <div className={`h-3 w-20 rounded-full overflow-hidden relative ${isDark ? "bg-white/5" : "bg-gray-200"}`}>
          <ShimmerBar isDark={isDark} />
        </div>
        <div className={`h-3 w-16 rounded-full overflow-hidden relative ${isDark ? "bg-white/5" : "bg-gray-200"}`}>
          <ShimmerBar isDark={isDark} />
        </div>
      </>
    )}
    <div className={`h-3 w-14 rounded-full overflow-hidden relative shrink-0 ${isDark ? "bg-white/5" : "bg-gray-200"}`}>
      <ShimmerBar isDark={isDark} />
    </div>
  </motion.div>
);

// ─── Skeleton Loader ─────────────────────────────────────────────────────────

const SkeletonLoader = ({ isMobile, isDark = true }) => (
  <div
    className="rounded-2xl border overflow-hidden"
    style={{
      background: isDark
        ? "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))"
        : "#f1f5f9",
      backdropFilter: isDark ? "blur(20px)" : "none",
      borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.10)",
      boxShadow: isDark
        ? "0 1px 0 0 rgba(255,255,255,0.06) inset, 0 24px 64px -12px rgba(0,0,0,0.4)"
        : "0 1px 0 0 rgba(255,255,255,0.9) inset, 0 1px 3px rgba(0,0,0,0.08), 0 4px 16px -4px rgba(0,0,0,0.04)",
    }}
  >
    <div className={`${isMobile ? "px-4 py-3.5" : "px-6 py-4"} flex justify-between items-center
                     ${isDark ? "border-white/8" : "border-gray-200"} border-b`}>
      <div className={`h-4 w-40 rounded-full overflow-hidden relative ${isDark ? "bg-white/5" : "bg-gray-200"}`}>
        <ShimmerBar isDark={isDark} />
      </div>
      <div className={`h-6 w-16 rounded-full overflow-hidden relative ${isDark ? "bg-white/5" : "bg-gray-200"}`}>
        <ShimmerBar isDark={isDark} />
      </div>
    </div>
    {Array.from({ length: 5 }).map((_, i) => (
      <SkeletonRow key={i} index={i} isMobile={isMobile} isDark={isDark} />
    ))}
  </div>
);

// ─── Empty State ──────────────────────────────────────────────────────────────

const EmptyState = ({ isMobile, onNewClick, isDark = true }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.97 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={spring}
    className="rounded-2xl border overflow-hidden"
    style={{
      background: isDark
        ? "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))"
        : "#f1f5f9",
      backdropFilter: isDark ? "blur(20px)" : "none",
      borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.10)",
      boxShadow: isDark
        ? "0 1px 0 0 rgba(255,255,255,0.06) inset, 0 24px 64px -12px rgba(0,0,0,0.4)"
        : "0 1px 0 0 rgba(255,255,255,0.9) inset, 0 1px 3px rgba(0,0,0,0.08), 0 4px 16px -4px rgba(0,0,0,0.04)",
    }}
  >
    <div className="flex flex-col items-center justify-center py-20 px-8 text-center gap-5">
      <div className="relative">
        <div className={`w-20 h-20 rounded-3xl flex items-center justify-center shadow-2xl
                        ${isDark
                          ? "bg-gradient-to-br from-white/8 to-white/4 border border-white/10 shadow-black/30"
                          : "bg-white border border-gray-200 shadow-gray-200"
                        }`}>
          <DollarSign size={32} className={isDark ? "text-gray-600" : "text-gray-400"} />
        </div>
        <motion.div
          className={`absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full border flex items-center justify-center
                     ${isDark ? "bg-gray-800 border-white/10" : "bg-white border-gray-200 shadow-sm"}`}
          animate={{ rotate: [0, 15, -15, 0] }}
          transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
        >
          <span className={isDark ? "text-gray-500 text-xs" : "text-gray-400 text-xs"}>?</span>
        </motion.div>
      </div>

      <div className="space-y-1.5">
        <p className={`font-semibold ${isMobile ? "text-base" : "text-lg"}
                       ${isDark ? "text-gray-300" : "text-gray-700"}`}
           style={{ letterSpacing: "-0.02em" }}>
          Nenhum lançamento encontrado
        </p>
        <p className={`text-sm max-w-xs ${isDark ? "text-gray-600" : "text-gray-500"}`}>
          Registre receitas e despesas para visualizá-las aqui
        </p>
      </div>

      <motion.button
        onClick={onNewClick}
        whileHover={{ scale: 1.04, y: -2, boxShadow: "0 12px 32px -8px rgba(16,185,129,0.4)" }}
        whileTap={{ scale: 0.96 }}
        transition={spring}
        className="flex items-center gap-2 px-5 py-2.5 rounded-xl
                   bg-gradient-to-r from-emerald-500 to-teal-500
                   text-white text-sm font-semibold
                   shadow-lg shadow-emerald-500/20"
      >
        <Plus size={15} strokeWidth={2.5} />
        Novo Lançamento
      </motion.button>
    </div>
  </motion.div>
);

// ─── Type Badge ───────────────────────────────────────────────────────────────

const TypeBadge = ({ item, isMobile, isDark = true }) => {
  const isReceita = item.type === "receita";
  const isVenda   = item.category?.includes("Venda");
  const isAluguel = item.category?.includes("Aluguel") || item.category?.includes("locação");

  let label, Icon, fromColorDark, toColorDark, fromColorLight, toColorLight, textColorDark, textColorLight;

  if (isVenda) {
    label = isMobile ? "Venda" : "Venda";
    Icon = ArrowUpRight;
    fromColorDark = "from-violet-500/20"; toColorDark = "to-purple-500/20";
    fromColorLight = "from-violet-100"; toColorLight = "to-purple-100";
    textColorDark = "text-violet-400"; textColorLight = "text-violet-700";
  } else if (isAluguel) {
    label = isMobile ? "Alug." : "Aluguel";
    Icon = ArrowUpRight;
    fromColorDark = "from-blue-500/20"; toColorDark = "to-sky-500/20";
    fromColorLight = "from-blue-100"; toColorLight = "to-sky-100";
    textColorDark = "text-blue-400"; textColorLight = "text-blue-700";
  } else if (isReceita) {
    label = isMobile ? "Rec." : "Receita";
    Icon = ArrowUpRight;
    fromColorDark = "from-emerald-500/20"; toColorDark = "to-teal-500/20";
    fromColorLight = "from-emerald-100"; toColorLight = "to-teal-100";
    textColorDark = "text-emerald-400"; textColorLight = "text-emerald-700";
  } else {
    label = isMobile ? "Desp." : "Despesa";
    Icon = ArrowDownRight;
    fromColorDark = "from-red-500/20"; toColorDark = "to-rose-500/20";
    fromColorLight = "from-red-100"; toColorLight = "to-rose-100";
    textColorDark = "text-red-400"; textColorLight = "text-red-700";
  }

  const fromColor = isDark ? fromColorDark : fromColorLight;
  const toColor = isDark ? toColorDark : toColorLight;
  const textColor = isDark ? textColorDark : textColorLight;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full
                  text-xs font-semibold
                  bg-gradient-to-r ${fromColor} ${toColor}
                  border ${isDark ? "border-white/8" : "border-gray-200"}
                  ${isDark ? "backdrop-blur-sm" : ""}
                  ${textColor}`}
    >
      <Icon size={10} strokeWidth={2.5} />
      {label}
    </span>
  );
};

// ─── Category Cell ────────────────────────────────────────────────────────────

const CategoryCell = ({ item, isMobile, isDark = true }) => {
  const Icon = categoryIcon(item.category);
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <div className={`shrink-0 w-7 h-7 rounded-lg border flex items-center justify-center
                      ${isDark
                        ? "bg-white/5 border-white/8"
                        : "bg-gray-100 border-gray-200"
                      }`}>
        <Icon size={12} className={isDark ? "text-gray-500" : "text-gray-500"} />
      </div>
      <div className="min-w-0">
        <p className={`font-medium truncate ${isMobile ? "text-xs" : "text-sm"}
                       ${isDark ? "text-gray-200" : "text-gray-800"}`}>
          {isMobile ? abbreviateCategory(item.category, 14) : item.category}
        </p>
        {item.description && !isMobile && (
          <p className={`text-xs truncate max-w-[180px] ${isDark ? "text-gray-600" : "text-gray-500"}`}>
            {item.description}
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Action Buttons ───────────────────────────────────────────────────────────

const Actions = ({ item, onExportSinglePDF, onShareClick, onDeleteClick, isMobile, isDark = true }) => {
  const size = isMobile ? 13 : 14;
  const base = isDark ? "text-gray-600" : "text-gray-400";
  const pdfHover = isDark
    ? "hover:text-indigo-400 hover:bg-indigo-500/10"
    : "hover:text-indigo-600 hover:bg-indigo-50";
  const waHover = isDark
    ? "hover:text-emerald-400 hover:bg-emerald-500/10"
    : "hover:text-emerald-600 hover:bg-emerald-50";
  const delHover = isDark
    ? "hover:text-red-400 hover:bg-red-500/10"
    : "hover:text-red-600 hover:bg-red-50";

  return (
    <div className="flex items-center justify-center gap-0.5">
      <Tooltip label="Exportar PDF" isDark={isDark}>
        <motion.button
          onClick={() => onExportSinglePDF?.(item)}
          whileHover={{ scale: 1.12 }}
          whileTap={{ scale: 0.9 }}
          transition={spring}
          className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${base} ${pdfHover}`}
        >
          <FileText size={size} />
        </motion.button>
      </Tooltip>

      <Tooltip label="WhatsApp" isDark={isDark}>
        <motion.button
          onClick={() => onShareClick?.(item)}
          whileHover={{ scale: 1.12 }}
          whileTap={{ scale: 0.9 }}
          transition={spring}
          className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${base} ${waHover}`}
        >
          <MessageCircle size={size} />
        </motion.button>
      </Tooltip>

      <Tooltip label="Excluir" isDark={isDark}>
        <motion.button
          onClick={() => onDeleteClick?.(item.id || item.uid)}
          whileHover={{ scale: 1.12 }}
          whileTap={{ scale: 0.9 }}
          transition={spring}
          className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${base} ${delHover}`}
        >
          <Trash2 size={size} />
        </motion.button>
      </Tooltip>
    </div>
  );
};

// ─── Desktop Row ──────────────────────────────────────────────────────────────

const DesktopRow = ({ item, index, onDeleteClick, onShareClick, onExportSinglePDF, isDark = true }) => {
  const isReceita = item.type === "receita";
  const isVenda   = item.category?.includes("Venda");
  const isAluguel = item.category?.includes("Aluguel") || item.category?.includes("locação");

  const valueColorDark = isVenda
    ? "text-violet-400"
    : isAluguel
    ? "text-blue-400"
    : isReceita
    ? "text-emerald-400"
    : "text-red-400";
  const valueColorLight = isVenda
    ? "text-violet-700"
    : isAluguel
    ? "text-blue-700"
    : isReceita
    ? "text-emerald-700"
    : "text-red-700";

  const valueColor = isDark ? valueColorDark : valueColorLight;
  const borderColor = isDark ? "border-white/5" : "border-gray-200";
  const hoverBg = isDark ? "bg-white/5" : "bg-gray-200/60";
  const dateColor = isDark ? "text-gray-500" : "text-gray-500";

  // 🔥 V3.0: Filas alternas (striped) en modo claro
  const rowBg = !isDark && index % 2 === 1 ? "bg-gray-100/70" : isDark ? "" : "bg-white";

  return (
    <motion.tr
      layout
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -16, transition: { duration: 0.15 } }}
      transition={{ ...spring, delay: index < 12 ? index * 0.03 : 0 }}
      whileHover="hover"
      className={`group relative ${rowBg} ${borderColor} border-b last:border-0`}
    >
      <td className="absolute inset-0 pointer-events-none" aria-hidden>
        <motion.div
          variants={{ hover: { opacity: 1 }, initial: { opacity: 0 } }}
          initial="initial"
          className={`absolute inset-0 ${hoverBg}`}
          transition={{ duration: 0.15 }}
        />
      </td>

      <td className={`px-6 py-3.5 text-xs whitespace-nowrap tabular-nums ${dateColor}`}>
        {formatDate(item.date, false)}
      </td>

      <td className="px-4 py-3.5">
        <CategoryCell item={item} isMobile={false} isDark={isDark} />
      </td>

      <td className={`px-4 py-3.5 text-right text-sm font-bold tabular-nums whitespace-nowrap ${valueColor}`}>
        {isReceita ? "+" : "−"}{fmt(Math.abs(item.value ?? 0))}
      </td>

      <td className="px-4 py-3.5">
        <TypeBadge item={item} isMobile={false} isDark={isDark} />
      </td>

      <td className="px-4 py-3.5">
        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <Actions
            item={item}
            onExportSinglePDF={onExportSinglePDF}
            onShareClick={onShareClick}
            onDeleteClick={onDeleteClick}
            isMobile={false}
            isDark={isDark}
          />
        </div>
      </td>
    </motion.tr>
  );
};

// ─── Mobile Card ──────────────────────────────────────────────────────────────

const MobileCard = ({ item, index, onDeleteClick, onShareClick, onExportSinglePDF, isDark = true }) => {
  const isReceita = item.type === "receita";
  const isVenda   = item.category?.includes("Venda");
  const isAluguel = item.category?.includes("Aluguel") || item.category?.includes("locação");

  const valueColorDark = isVenda
    ? "text-violet-400"
    : isAluguel
    ? "text-blue-400"
    : isReceita
    ? "text-emerald-400"
    : "text-red-400";
  const valueColorLight = isVenda
    ? "text-violet-700"
    : isAluguel
    ? "text-blue-700"
    : isReceita
    ? "text-emerald-700"
    : "text-red-700";

  const valueColor = isDark ? valueColorDark : valueColorLight;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20, transition: { duration: 0.15 } }}
      transition={{ ...spring, delay: index < 10 ? index * 0.04 : 0 }}
      className="mx-3 mb-2 last:mb-3"
    >
      <motion.div
        whileHover={isDark
          ? { y: -2, boxShadow: "0 12px 32px -8px rgba(0,0,0,0.5)" }
          : { y: -2, boxShadow: "0 6px 20px -6px rgba(0,0,0,0.10)" }
        }
        transition={spring}
        className="rounded-xl border p-4"
        style={{
          background: isDark
            ? "linear-gradient(to bottom right, rgba(255,255,255,0.05), rgba(255,255,255,0.02))"
            : "#ffffff",
          backdropFilter: isDark ? "blur(8px)" : "none",
          borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.10)",
          boxShadow: isDark
            ? "none"
            : "0 1px 3px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={`shrink-0 w-8 h-8 rounded-xl border flex items-center justify-center
                            ${isDark
                              ? "bg-white/5 border-white/8"
                              : "bg-gray-100 border-gray-200"
                            }`}>
              {React.createElement(categoryIcon(item.category), {
                size: 14,
                className: isDark ? "text-gray-500" : "text-gray-500",
              })}
            </div>
            <div className="min-w-0">
              <p className={`text-sm font-semibold truncate ${isDark ? "text-gray-200" : "text-gray-800"}`}
                 style={{ letterSpacing: "-0.01em" }}>
                {abbreviateCategory(item.category, 18)}
              </p>
              <p className={`text-xs mt-0.5 tabular-nums ${isDark ? "text-gray-600" : "text-gray-500"}`}>
                {formatDate(item.date, true)}
              </p>
            </div>
          </div>

          <div className="shrink-0 text-right">
            <p className={`text-sm font-bold tabular-nums ${valueColor}`}>
              {isReceita ? "+" : "−"}{fmt(Math.abs(item.value ?? 0))}
            </p>
            <div className="mt-1 flex justify-end">
              <TypeBadge item={item} isMobile isDark={isDark} />
            </div>
          </div>
        </div>

        <div className={`mt-3 pt-3 border-t flex justify-end
                        ${isDark ? "border-white/5" : "border-gray-200"}`}>
          <Actions
            item={item}
            onExportSinglePDF={onExportSinglePDF}
            onShareClick={onShareClick}
            onDeleteClick={onDeleteClick}
            isMobile
            isDark={isDark}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const FinanceTable = ({
  isMobile,
  isDark = true,
  loading,
  registrosFiltrados,
  onDeleteClick,
  onShareClick,
  onExportSinglePDF,
  onNewClick,
}) => {
  const registros = useMemo(() => {
    if (!Array.isArray(registrosFiltrados) || registrosFiltrados.length === 0) return [];
    const seen = new Map();
    for (const item of registrosFiltrados) {
      const key = item?.id ?? item?.uid ?? JSON.stringify(item);
      if (key && !seen.has(key)) seen.set(key, item);
    }
    return Array.from(seen.values());
  }, [registrosFiltrados]);

  if (loading) return <SkeletonLoader isMobile={isMobile} isDark={isDark} />;
  if (registros.length === 0) return <EmptyState isMobile={isMobile} onNewClick={onNewClick} isDark={isDark} />;

  const headerBg = isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.03)";
  const headerBorder = isDark ? "border-white/8" : "border-gray-200";
  const headerTitle = isDark ? "text-gray-300" : "text-gray-800";
  const headerBadgeBg = isDark ? "bg-emerald-500/10" : "bg-emerald-100";
  const headerBadgeText = isDark ? "text-emerald-400" : "text-emerald-700";
  const headerBadgeBorder = isDark ? "border-emerald-500/15" : "border-emerald-300";
  const thColor = isDark ? "text-gray-600" : "text-gray-500";
  const thBorder = isDark ? "border-white/6" : "border-gray-200";
  const thBg = isDark ? "rgba(10,10,15,0.85)" : "rgba(255,255,255,0.95)";
  const footerBg = isDark ? "rgba(255,255,255,0.01)" : "rgba(0,0,0,0.02)";
  const footerBorder = isDark ? "border-white/6" : "border-gray-200";
  const footerText = isDark ? "text-gray-700" : "text-gray-500";
  const dotInactive = isDark ? "bg-white/15" : "bg-gray-300";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={spring}
      className="rounded-2xl border overflow-hidden"
      style={{
        background: isDark
          ? "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))"
          : "#f1f5f9",
        backdropFilter: isDark ? "blur(20px)" : "none",
        WebkitBackdropFilter: isDark ? "blur(20px)" : "none",
        borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.10)",
        boxShadow: isDark
          ? "0 1px 0 0 rgba(255,255,255,0.06) inset, 0 24px 64px -12px rgba(0,0,0,0.4)"
          : "0 1px 0 0 rgba(255,255,255,0.9) inset, 0 1px 3px rgba(0,0,0,0.08), 0 4px 16px -4px rgba(0,0,0,0.04)",
      }}
    >
      <div
        className={`${isMobile ? "px-4 py-3.5" : "px-6 py-4"} flex justify-between items-center
                    ${headerBorder} border-b`}
        style={{
          background: headerBg,
          backdropFilter: isDark ? "blur(16px)" : "none",
        }}
      >
        <div className={`absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent ${isDark ? "via-white/10" : "via-black/5"} to-transparent pointer-events-none`} />

        <div className="flex items-center gap-2.5">
          <div className={`w-6 h-6 rounded-lg border flex items-center justify-center
                          ${isDark
                            ? "bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-emerald-500/20"
                            : "bg-gradient-to-br from-emerald-100 to-teal-100 border-emerald-300"
                          }`}>
            <DollarSign size={12} className={isDark ? "text-emerald-400" : "text-emerald-600"} />
          </div>
          <h3 className={`font-semibold text-sm ${headerTitle}`} style={{ letterSpacing: "-0.01em" }}>
            Lançamentos Financeiros
          </h3>
        </div>

        <motion.span
          key={registros.length}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={spring}
          className={`px-2.5 py-1 rounded-full text-xs font-semibold border
                     ${headerBadgeBg} ${headerBadgeText} ${headerBadgeBorder}`}
        >
          {registros.length} {registros.length !== 1 ? "registros" : "registro"}
        </motion.span>
      </div>

      {isMobile ? (
        <div
          className={`overflow-y-auto py-3 ${isDark ? "scrollbar-thumb-white/10" : "scrollbar-thumb-gray-300"} scrollbar-track-transparent`}
          style={{ maxHeight: "520px" }}
        >
          <AnimatePresence initial={false}>
            {registros.map((item, i) => (
              <MobileCard
                key={item?.id || item?.uid || `row-${i}`}
                item={item}
                index={i}
                onDeleteClick={onDeleteClick}
                onShareClick={onShareClick}
                onExportSinglePDF={onExportSinglePDF}
                isDark={isDark}
              />
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <div
          className={`overflow-y-auto overflow-x-auto ${isDark ? "scrollbar-thumb-white/10" : "scrollbar-thumb-gray-300"} scrollbar-track-transparent`}
          style={{ maxHeight: "560px" }}
        >
          <table className="w-full border-collapse">
            <thead
              className="sticky top-0 z-20"
              style={{
                background: thBg,
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
              }}
            >
              <tr className={`border-b ${thBorder}`}>
                {["Data", "Categoria", "Valor", "Tipo", "Ações"].map((label) => (
                  <th
                    key={label}
                    className={`px-4 py-3 text-left first:pl-6 last:pr-6
                                text-xs font-semibold uppercase tracking-wider
                                ${thColor}`}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              <AnimatePresence initial={false}>
                {registros.map((item, i) => (
                  <DesktopRow
                    key={item?.id || item?.uid || `row-${i}`}
                    item={item}
                    index={i}
                    onDeleteClick={onDeleteClick}
                    onShareClick={onShareClick}
                    onExportSinglePDF={onExportSinglePDF}
                    isDark={isDark}
                  />
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      )}

      {registros.length > 5 && (
        <div className={`px-6 py-3 ${footerBorder} border-t flex justify-between items-center`}
             style={{ background: footerBg }}>
          <span className={`text-xs ${footerText}`}>
            {registros.length} lançamento{registros.length !== 1 ? "s" : ""}
          </span>
          <div className="flex gap-1">
            {Array.from({ length: Math.min(5, Math.ceil(registros.length / 10)) }).map((_, i) => (
              <div key={i} className={`w-1 h-1 rounded-full ${i === 0 ? "bg-emerald-500" : dotInactive}`} />
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default FinanceTable;